import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Clock } from "lucide-react";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { formatTimestamp, getUserName } from "../utils/helpers";

interface ActivityLogProps {
    logs: any[];
    entityName?: string;
    users: any[];
    currentUser?: any;
}

export function ActivityLog({ logs, entityName = "item", users, currentUser }: ActivityLogProps) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Activity Log</CardTitle>
                <CardDescription>History of changes to this {entityName}</CardDescription>
            </CardHeader>
            <CardContent>
                {logs && logs.length > 0 ? (
                    <div className="space-y-4">
                        {logs.sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((entry: any) => (
                            <div key={entry.id} className="flex items-start gap-3 pb-4 border-b last:border-b-0">
                                <div className="bg-muted rounded-full p-1.5 mt-0.5"><Clock size={14} className="text-muted-foreground" /></div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium mb-2">
                                        {entry.changeDescription.includes("|||") || entry.changeDescription.includes(' changed to "') ? (
                                            <div className="flex flex-col gap-2 mt-1">
                                                {entry.changeDescription.split("|||").map((change: string, i: number) => {
                                                    const match = change.match(/(.*?) changed to "(.*?)"/);
                                                    if (match) {
                                                        const [_, field, newVal] = match;
                                                        return (
                                                            <div key={i} className="flex items-center flex-wrap gap-2">
                                                                <Badge variant="outline" className="capitalize bg-white text-gray-700 border-gray-200 shadow-sm">
                                                                    {field}
                                                                </Badge>
                                                                {/* <ArrowRight size={12} className="text-gray-400" /> */}
                                                                <span>changed to</span>
                                                                <span className="text-green-600 font-medium text-xs bg-green-50 px-1.5 py-0.5 rounded border border-green-100">
                                                                    {newVal}
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                    return <div key={i}>{change}</div>;
                                                })}
                                            </div>
                                        ) : entry.changeDescription.includes(" was updated to ") ? (
                                            <span className="text-muted-foreground">
                                                {entry.changeDescription}
                                            </span>
                                        ) : entry.changeDescription.match(/Marked '(.*?)' as '(.*?)' for procedure: (.*)/) ? (
                                            (() => {
                                                const match = entry.changeDescription.match(/Marked '(.*?)' as '(.*?)' for procedure: (.*)/);
                                                const [_, field, value, procedure] = match!;
                                                return (
                                                    <div className="flex items-center flex-wrap gap-1">
                                                        <span>Marked</span>
                                                        <Badge variant="outline" className="capitalize bg-white text-gray-700 border-gray-200 shadow-sm">
                                                            {field}
                                                        </Badge>
                                                        <span>as</span>
                                                        <span className="text-green-600 font-medium text-xs bg-green-50 px-1.5 py-0.5 rounded border border-green-100">
                                                            {value}
                                                        </span>
                                                        <span>for procedure: {procedure}</span>
                                                    </div>
                                                );
                                            })()
                                        ) : entry.changeDescription.match(/TraineeNotes updated for procedure: (.*)/) ? (
                                            (() => {
                                                const match = entry.changeDescription.match(/TraineeNotes updated for procedure: (.*)/);
                                                const [_, procedure] = match!;
                                                return (
                                                    <div className="flex items-center flex-wrap gap-1">
                                                        <Badge variant="outline" className="capitalize bg-white text-gray-700 border-gray-200 shadow-sm">
                                                            TraineeNotes
                                                        </Badge>
                                                        <span>updated for procedure: {procedure}</span>
                                                    </div>
                                                );
                                            })()
                                        ) : entry.changeDescription.includes(" for activity ") ? (
                                            <>
                                                <span className="text-muted-foreground">
                                                    {entry.changeDescription.split(" for activity ")[0]}
                                                </span>
                                                <span className="text-muted-foreground font-medium">
                                                    {" for activity "}
                                                </span>
                                                <span className="text-muted-foreground">
                                                    {entry.changeDescription.split(" for activity ")[1]}
                                                </span>
                                            </>
                                        ) : entry.changeDescription.match(/Password updated by "(.*?)"/) ? (
                                            (() => {
                                                const match = entry.changeDescription.match(/Password updated by "(.*?)"/);
                                                const [_, email] = match!;
                                                return (
                                                    <div className="flex items-center flex-wrap gap-1">
                                                        <Badge variant="outline" className="capitalize bg-white text-gray-700 border-gray-200 shadow-sm">
                                                            Password
                                                        </Badge>
                                                        <span>updated by {email}</span>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            entry.changeDescription
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <p className="text-xs text-muted-foreground">{formatTimestamp(entry.timestamp)}</p>
                                        <Separator orientation="vertical" className="h-3" />
                                        <p className="text-xs text-muted-foreground">By {typeof entry.user === 'object' ? entry.user?.name : getUserName(entry.user, users, currentUser)}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-muted-foreground italic">No activity history available.</p>
                )}
            </CardContent>
        </Card>
    );
}
